import React from "react";
import { FiAward, FiCheckCircle, FiCode, FiCompass, FiGrid, FiLayers, FiPenTool, FiRepeat, FiSliders, FiTarget } from "react-icons/fi";
import {
  LuSettings,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuBuilding2,
  LuShoppingBag,
  LuClock,
  LuWallet,
  LuCpu,
  LuClipboardList,
  LuPalette,
  LuCode,
  LuBug,
  LuRocket,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const uiDesignSystemsHeroData = {
  backgroundImage: "/images/serviceBanners/UIDesignSystem.webp",
  whiteImage: "/images/serviceBanners/UIDesignsystemW.webp",
  subTitle: "Creating Scalable UI Design Systems for Better Digital Experiences",
  title: (
    <>
      Modern <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">UI Design System</span> Solutions
    </>
  ),
  description:
    "We build scalable UI design systems that ensure consistency, usability, and seamless user experiences across digital products by standardizing components, improving design efficiency, and enhancing overall product quality.",
  primaryButton: {
    title: "Start Your Design System",
    onClick: () => console.log("System Start clicked"),
  },
  secondaryButton: {
    title: "View Design Library",
    onClick: () => console.log("View Library clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Design Consistency",
      desc: "Scalable UI systems built for consistent user experiences.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Reusable Components",
      desc: "Structured design components for faster product development.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Scalable Interface Systems",
      desc: "Flexible UI frameworks designed for growing digital products.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "User-Centered Design",
      desc: "Modern interfaces focused on clarity, usability, and engagement.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/reactjs.svg",
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(168,85,247,0.35)",
      iconGlow: "rgba(168,85,247,0.9)",
    },
  ],
};

// 2. Stats Block
export const uiDesignStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const uiDesignSolutionsAndProcessData = {
  buildSubtitle: "SYSTEM-DRIVEN QUALITY",
  buildTitle: "Custom UI Design System Solutions for Scalable Digital Products",
  buildCards: [
    {
      title: "Component-Based Design Systems",
      desc: "Scalable UI components designed for consistency across platforms.",
      icon: FiLayers,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Enterprise UI Solutions",
      desc: "Structured design systems built for complex digital products.",
      icon: FiSliders,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce Interface Design",
      desc: "User-friendly UI systems focused on smoother shopping experiences.",
      icon: FiCompass,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "On-Demand Platform Interfaces",
      desc: "Flexible UI frameworks for booking and service-based platforms.",
      icon: FiLayers,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech UI Experiences",
      desc: "Secure and intuitive interfaces designed for financial products.",
      icon: FiSliders,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Custom UI Design Systems",
      desc: "Tailored design systems aligned with product goals and brand consistency.",
      icon: FiPenTool,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR GOVERNANCE FLOW",
  processTitle: "Our UI Design System Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Research",
      description: "Understand product goals, brand needs, and interface requirements.",
      icon: LuUsers,
    },
    {
      step: "02",
      title: "System Planning",
      description: "Define design standards, UI patterns, and component structure.",
      icon: LuPalette,
    },
    {
      step: "03",
      title: "UI Component Design",
      description: "Create reusable and consistent interface components.",
      icon: LuCode,
    },
    {
      step: "04",
      title: "Design System Development",
      description: "Build scalable UI frameworks for consistent digital experiences.",
      icon: LuRocket,
    },
    {
      step: "05",
      title: "Testing & Validation",
      description: "Review usability, consistency, and design performance.",
      icon: LuPalette,
    },
    {
      step: "06",
      title: "Optimization & Support",
      description: "Refine design systems for long-term scalability and usability.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR UI DESIGN SYSTEM SERVICES?",
  benefitsTitle: "Why Businesses Choose Our UI Design System Solutions",
  benefits: [
    "Consistent UI across platforms with scalable design systems",
    "Faster development through reusable components and patterns",
    "Modern interface standards for better user experience",
    "Optimized performance through structured UI architecture",
    "Efficient workflow using standardized design systems",
    "Highly scalable design frameworks for growing products",
    "Cost-effective UI development with reduced redesign effort",
  ],
  logoImage: "/images/serviceBanners/UIDesignSystem.webp",
  whiteLogoImage: "/images/serviceBanners/UIDesignsystemW.webp",
};

// 4. Portfolio Data
export const uiDesignPortfolioData = {
  subtitle: "OUR LIBRARIES",
  title: "Systems Designed for Scale",
  buttonText: "View Documentation",
  buttonLink: "#",
  apps: [
    {
      title: "Nexus Core System",
      badge: "Design System",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "A full-scale design library for a multi-product fintech ecosystem.",
      downloads: "500+ Atoms",
      rating: "5.0",
      image: "https://images.unsplash.com/photo-1545235617-9465d2a55698?q=80&w=2080&auto=format&fit=crop",
    },
  ],
};

// 5. Technologies Data
export const uiDesignTechnologiesData = {
  techTitle: "OUR SYSTEM TOOL STACK",
  technologies: [
    { name: "Figma", icon: "Palette" },
    { name: "Zeroheight", icon: "Layout" },
    { name: "Storybook", icon: "Code" },
    { name: "Tokens Studio", icon: "Settings" },
    { name: "Lottie", icon: "Zap" },
  ],
  trustTitle: "TRUSTED BY PRODUCT TEAMS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

export const uiDesignAboutData = [
  {
    title: "Scalable UI Design Systems for Modern Digital Products",
    content: (
      <>
        Modern digital platforms require structured and consistent interfaces to maintain better usability and seamless product experiences. Professional{" "}
        <b className="text-text-primary font-semibold">design system services</b> help businesses create unified digital ecosystems that improve collaboration between design and
        development teams.
        <b className="text-text-primary font-semibold">Tycho Technologies</b> focuses on building scalable interface strategies that support long term product growth, improve
        workflow efficiency, and maintain visual consistency across multiple digital environments.
      </>
    ),
  },
  {
    title: "Structured UI Frameworks for Better Product Experiences",
    content: (
      <>
        Businesses developing large scale applications require flexible interface systems that simplify product expansion and feature management. A reliable{" "}
        <b className="text-text-primary font-semibold">ui design system company</b> helps organizations establish standardized design guidelines, interaction patterns, and scalable
        interface structures for modern digital products. Companies also invest in{" "}
        <a href="https://tychotechnologies.com/mobile-app-design-india" className="text-cyan-400 underline hover:text-cyan-300">
          mobile app design services
        </a>{" "}
        to maintain consistent experiences across mobile platforms while improving accessibility, usability, and user interaction for growing digital audiences.
      </>
    ),
  },
  {
    title: "Advanced Component Strategies for Scalable Interfaces",
    content: (
      <>
        Modern digital products rely on organized design workflows that support continuous product evolution and faster collaboration. Professional{" "}
        <b className="text-text-primary font-semibold">scalable ui component design solutions</b> help businesses streamline interface management, improve development alignment,
        and reduce design inconsistencies across platforms. Organizations also collaborate with experts providing{" "}
        <a href="https://tychotechnologies.com/wireframing-prototyping-india" className="text-cyan-400 underline hover:text-cyan-300">
          wireframing and prototyping services
        </a>{" "}
        to validate interface concepts, improve product structure, and optimize digital experiences before implementation begins.
      </>
    ),
  },
  {
    title: "Reliable UI Design System Services in India",
    content: (
      <>
        The demand for structured digital experiences continues to grow across SaaS, fintech, healthcare, enterprise, and ecommerce industries. Businesses searching for scalable
        interface frameworks often choose trusted <b className="text-text-primary font-semibold">enterprise design system solutions</b> providers to maintain long term product
        consistency and improve operational scalability. Tycho Technologies also helps brands looking for a reliable{" "}
        <b className="text-text-primary font-semibold">design system company in india</b> with future focused interface strategies and modern digital workflows. Companies
        additionally rely on professional <b className="text-text-primary font-semibold">ui design services india</b> providers to strengthen digital product experiences and
        support sustainable platform growth.
      </>
    ),
  },
];
// 7. FAQs (Preserved)
export const uiDesignSystemsFaqs = [
  {
    q: "Why does our product need a design system?",
    a: "A design system keeps your UI consistent, reduces repetitive work, and helps design and development teams ship faster with fewer inconsistencies.",
  },
  {
    q: "Can you build a design system for an existing product?",
    a: "Yes. We can audit existing screens, extract common patterns, and convert them into a structured reusable system without disrupting ongoing delivery.",
  },
  {
    q: "Will we get reusable components in Figma?",
    a: "Absolutely. You receive a well-organized Figma library with component variants, states, and usage guidance for your team.",
  },
  {
    q: "Do you also support developer implementation?",
    a: "Yes. We provide handoff-ready specs, states, and design rationale so engineering teams can implement the system with confidence.",
  },
  {
    q: "How long does a UI design system project typically take?",
    a: "Most projects take 3 to 8 weeks depending on product complexity, number of components, and required documentation depth.",
  },
];
